import { ResultDigits } from "../components/ResultDigits";
import React, { useEffect, useState, useCallback } from "react";
import { Trophy, Filter, Ticket, MessageCircle, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { shareResultSheet } from "../lib/resultSheet";
import { shareResultImage } from "../lib/resultImage";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { CardSkeletonList, EmptyState } from "../components/Common";
import { dateLabel, dayBucket, dayBucketLabel } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShowTimeline } from "../components/ShowTimeline";
import { LuckyToday } from "../components/LuckyToday";
import { KeralaPanel } from "../components/KeralaPanel";
import { keralaOfficial, isOfficialKerala } from "../lib/kerala";
import { fmt, rateLine } from "../lib/format";

const to12h = (tm) => { if (!tm) return ""; const [h, m] = tm.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const categoryOf = (bt) => { const b = bt || ""; if (b.startsWith("4D")) return "4D"; if (b.startsWith("3D")) return "3D"; return b === "ALL" || b.length === 1 ? "Singles" : "Doubles"; };
/* Settled tickets grouped per show/draw, then per category (Singles · Doubles · 3D · 4D) */
function groupOrders(bets) {
  const map = new Map();
  for (const b of bets) {
    const key = `${b.game_id}|${b.draw_date || ""}|${b.draw_time || ""}`;
    if (!map.has(key)) map.set(key, { key: key.replace(/[^a-zA-Z0-9]/g, "_"), game_name: b.game_name, draw_date: b.draw_date, draw_time: b.draw_time, result: null, bets: [], byCat: {}, total: 0, won: 0 });
    const g = map.get(key);
    g.bets.push(b); g.total += b.amount || 0; if (b.status === "won") g.won += b.win_amount || 0;
    if (b.result_number) g.result = String(b.result_number);
    (g.byCat[categoryOf(b.board_type)] = g.byCat[categoryOf(b.board_type)] || []).push(b);
  }
  return [...map.values()].sort((x, y) => (`${x.draw_date}${x.draw_time}` < `${y.draw_date}${y.draw_time}` ? 1 : -1));
}

export default function Result() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("history");
  const [sharing, setSharing] = useState(false);
  const [results, setResults] = useState([]);
  const [bets, setBets] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameFilter, setGameFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [catFilter, setCatFilter] = useState(() => (new URLSearchParams(window.location.search).get("cat") === "Kerala" ? "Kerala" : "all"));

  const load = useCallback(async (silent = false) => {
    if (silent !== true) setLoading(true);
    try {
      const params = {};
      if (gameFilter !== "all") params.game_id = gameFilter;
      if (dateFilter) params.date = dateFilter;
      const [r, g] = await Promise.all([api.get("/results", { params }), api.get("/games")]);
      setResults(r.data);
      setGames(g.data);
      if (user) {
        const b = await api.get("/bets/my", { params: { tab: "completed" } });
        setBets(b.data);
      }
    } catch {}
    setLoading(false);
  }, [gameFilter, dateFilter, user]);

  useEffect(() => { load(); const t = setInterval(() => load(true), 20000); return () => clearInterval(t); }, [load]);
  const intervalIds = new Set(games.filter((g) => g.mode === "interval").map((g) => g.id));
  const keralaIds = new Set(games.filter((g) => g.category === "Kerala").map((g) => g.id));
  const gameById = Object.fromEntries(games.map((g) => [g.id, g]));
  const fixedResults = (gameFilter !== "all" && intervalIds.has(gameFilter) ? [] : results.filter((r) => !intervalIds.has(r.game_id))).filter((r) => catFilter !== "Kerala" || keralaIds.has(r.game_id));

  return (
    <div className="space-y-4">
      <div className="flex rl-card p-1">
        <button onClick={() => setTab("history")} data-testid="result-tab-history" className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${tab === "history" ? "rl-gold-bg" : "text-slate-500"}`}>{t("result_history")}</button>
        <button onClick={() => setTab("orders")} data-testid="result-tab-orders" className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${tab === "orders" ? "rl-gold-bg" : "text-slate-500"}`}>{t("my_orders")}</button>
      </div>

      {tab === "history" && (
        <>
          <div className="flex gap-2">
          <button onClick={async () => { setSharing(true); try { await shareResultSheet(lang === "ta"); } catch { toast.error("Could not build the result sheet"); } finally { setSharing(false); } }} disabled={sharing}
            className="flex-1 bg-[#25D366] text-white font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60" data-testid="result-sheet-share-btn">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}{lang === "ta" ? "📲 WhatsApp-ல் பகிர்" : "📲 Share on WhatsApp"}
          </button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/results/sheet.pdf${dateFilter ? `?date=${dateFilter}` : ""}`} target="_blank" rel="noreferrer" className="bg-slate-800 text-white font-bold py-3 px-4 rounded-2xl text-sm flex items-center justify-center gap-1" data-testid="result-sheet-pdf-btn">📄 PDF</a>
          </div>
          <div className="flex gap-2" data-testid="result-cat-chips">
            <button onClick={() => setCatFilter("all")} className={`flex-1 py-2 rounded-xl text-xs font-black border transition-colors ${catFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "rl-card text-slate-600 border-slate-200"}`} data-testid="result-cat-all">{t("all_games")}</button>
            <button onClick={() => setCatFilter("Kerala")} className={`flex-1 py-2 rounded-xl text-xs font-black border transition-colors ${catFilter === "Kerala" ? "bg-emerald-700 text-white border-emerald-700" : "rl-card text-slate-600 border-slate-200"}`} data-testid="result-cat-kerala">🏛️ {lang === "ta" ? "கேரளா மட்டும்" : "Kerala only"}</button>
          </div>
          {catFilter === "Kerala" && <KeralaPanel games={games} results={results} lang={lang} />}
          {catFilter !== "Kerala" && <ShowTimeline />}
          {catFilter !== "Kerala" && (gameFilter === "all" || games.find((g) => g.id === gameFilter)?.mode === "interval") && games.filter((g) => g.mode === "interval" && (gameFilter === "all" || g.id === gameFilter)).map((g) => <LuckyToday key={g.id} game={g} date={dateFilter || undefined} />)}
          <div className="flex gap-2">
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="rl-card border-slate-200 text-slate-900 text-xs h-11" data-testid="result-game-filter"><Filter className="w-3.5 h-3.5 mr-1 text-blue-600" /><SelectValue placeholder="Game" /></SelectTrigger>
              <SelectContent className="rl-glass border-slate-200 text-slate-900">
                <SelectItem value="all">{t("all_games")}</SelectItem>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="rl-card px-3 h-11 text-slate-900 text-xs outline-none flex-1" data-testid="result-date-filter" />
          </div>

          {loading ? <CardSkeletonList count={5} /> : fixedResults.length === 0 ? (
            <EmptyState icon={Trophy} title={t("no_results_yet")} subtitle={t("results_appear")} />
          ) : (
            <div className="space-y-3" data-testid="result-groups">
              {Object.entries(fixedResults.reduce((acc, r) => { const k = `${r.draw_date}|${r.draw_time}`; (acc[k] = acc[k] || []).push(r); return acc; }, {})).map(([k, rows]) => {
                const [d, tm] = k.split("|");
                const [h, m] = tm.split(":").map(Number);
                const t12 = `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
                return (
                  <div key={k} data-testid={`result-group-${k.replace(/[|:]/g, "-")}`}>
                    <div className="flex items-center gap-2 mb-1.5 px-1"><span className="text-[10px] font-black tracking-widest text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">{t12}</span><span className="text-[11px] text-slate-500">{dateLabel(d)} · {rows.length} {rows.length === 1 ? "show" : "shows"}</span></div>
                    <div className="space-y-2">
                      {rows.map((r) => (
                        <div key={r.id} className="rl-card p-3.5 flex items-center justify-between gap-2" data-testid={`result-item-${r.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="font-display font-bold text-slate-900 text-sm truncate">{r.game_name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{dateLabel(r.draw_date)} · {r.draw_time} · #{r.issue_id}</div>
                            {isOfficialKerala(gameById[r.game_id]) && <div className="text-[10px] font-semibold text-emerald-800 truncate" data-testid={`kerala-official-line-${r.id}`}>🏛️ {keralaOfficial(r.draw_date)?.label} · {keralaOfficial(r.draw_date)?.weekday}</div>}
                            {Array.isArray(r.pattern_pass) && r.pattern_pass.length > 0 && <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-black text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`pattern-pass-${r.id}`}>✓ Pattern passed on {r.pattern_pass.join(" · ")}</div>}
                          </div>
                          <ResultDigits number={r.winning_number} size="w-9 h-9 text-base" className="shrink-0" testId={`result-digits-${r.id}`} />
                          <button onClick={() => shareResultImage(r, lang === "ta")} className="shrink-0 w-9 h-9 rounded-xl bg-[#25D366] text-white flex items-center justify-center active:scale-95 transition-transform" title="Share result image" data-testid={`share-result-img-${r.id}`}><Share2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "orders" && (
        !user ? <EmptyState icon={Ticket} title={t("login_required")} subtitle={t("login_to_view_orders")} /> :
        loading ? <CardSkeletonList count={4} /> : bets.length === 0 ? (
          <EmptyState icon={Ticket} title={t("no_completed_orders")} subtitle={t("settled_bets_here")} />
        ) : (
          <div className="space-y-3" data-testid="orders-groups">
            {groupOrders(bets).map((g, i, arr) => (
              <React.Fragment key={g.key}>
              {(i === 0 || dayBucket(arr[i - 1].draw_date) !== dayBucket(g.draw_date)) && (
                <div className="flex items-center gap-2 pt-1" data-testid={`order-day-header-${dayBucket(g.draw_date) || "earlier"}`}>
                  <span className={`text-[10px] font-black tracking-widest rounded-full px-2.5 py-0.5 ${dayBucket(g.draw_date) === "today" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600"}`}>{dayBucketLabel(dayBucket(g.draw_date), lang)}</span>
                  <span className="text-[10px] text-slate-400">{dateLabel(g.draw_date)}</span>
                </div>
              )}
              <div className="rl-card overflow-hidden" data-testid={`order-group-${g.key}`}>
                <div className="px-3.5 py-2.5 bg-slate-900 text-white flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm truncate">{g.game_name}{g.draw_time ? ` · ${to12h(g.draw_time)}` : ""}</div>
                    <div className="text-[10px] text-slate-300">{dateLabel(g.draw_date)} · {g.bets.length} {t("tickets")} · {t("bet")} {fmt(g.total)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {g.result && <ResultDigits number={g.result} size="w-7 h-7 text-sm" testId={`order-result-${g.key}`} />}
                    <div className={`text-[11px] font-black mt-0.5 ${g.won > 0 ? "text-emerald-300" : "text-red-300"}`} data-testid={`order-outcome-${g.key}`}>{g.won > 0 ? `${t("won")} ${fmt(g.won)}` : (lang === "ta" ? "வெற்றி இல்லை" : "No win")}</div>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {["Singles", "Doubles", "3D", "4D"].filter((c) => g.byCat[c]).map((c) => (
                    <div key={c} className="px-3.5 py-2" data-testid={`order-cat-${g.key}-${c}`}>
                      <div className="text-[10px] font-black tracking-widest text-slate-500 mb-1">{c} · {g.byCat[c].length}</div>
                      <div className="space-y-1">
                        {g.byCat[c].map((b) => (
                          <div key={b.id} className="flex items-center gap-2 text-xs" data-testid={`order-item-${b.id}`}>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 w-9 text-center">{b.board_type === "ALL" ? "ALL" : b.board_type || b.slat_name}</span>
                            <span className={`rl-ball w-8 h-8 text-xs ${b.bet_mode === "box" ? "ring-2 ring-amber-400" : ""}`}>{b.number}</span>
                            <span className="text-slate-600 font-semibold" data-testid={`order-rate-${b.id}`}>{rateLine(b)}</span>
                            <span className={`ml-auto text-[10px] font-black px-2 py-0.5 rounded-full ${b.status === "won" ? "bg-emerald-100 text-emerald-700" : "bg-red-50 text-red-500"}`}>{b.status === "won" ? `WON ${fmt(b.win_amount)}` : "LOST"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
        )
      )}
    </div>
  );
}
